import React, { useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import { FilterPanel } from '@gsa/afp-component-library';
import {
  GET_MAKES_BY_PARTIAL_MATCH,
  GET_MODELS_BY_PARTIAL_MATCH,
} from '../../make-model-approval/queries.gql';

const CONTEXT = 'CPBBC';

const BidHistoryTableFilter = ({ filterStructureData, setFilters }) => {
  const [selectedMakeCode, setSelectedMakeCode] = useState('');
  // const [selectedBodyMakeCode, setSelectedBodyMakeCode] = useState('');
  const [selectedChassisMakeCode, setSelectedChassisMakeCode] = useState('');
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [chassesMakes, setChassesMakes] = useState([]);
  const [chassesModels, setChassesModels] = useState([]);
  const [typeaheadData, setTypeaheadData] = useState([]);

  const StandardItems = React.useMemo(() => {
    const items = [];
    (filterStructureData ?? []).forEach((bid) => {
      bid.standardItems?.forEach((si) => {
        const sin = si.standardItemNumber;
        const exist = items.find((item) => item.sin === sin);
        if (!exist) items.push({ sin, ids: new Set([si.id]) });
        else exist.ids.add(si.id);
      });
    });
    return items;
  }, [filterStructureData]);

  const [getMakesByPartialName] = useLazyQuery(GET_MAKES_BY_PARTIAL_MATCH, {
    onCompleted: (data) => {
      const values = data?.getMakesByPartialMatch?.map(
        ({ makeName }) => makeName,
      );
      setMakes(data?.getMakesByPartialMatch);
      setTypeaheadData({ field: 'makeCode', values });
    },
  });

  const [getModelsByPartialName] = useLazyQuery(GET_MODELS_BY_PARTIAL_MATCH, {
    onCompleted: (data) => {
      const values = data?.getModelsByPartialMatch?.map(
        ({ modelName }) => modelName,
      );
      setModels(data?.getModelsByPartialMatch);
      setTypeaheadData({ field: 'modelCode', values });
    },
  });

  const [getChassisMakesByPartialName] = useLazyQuery(
    GET_MAKES_BY_PARTIAL_MATCH,
    {
      onCompleted: (data) => {
        const values = data?.getMakesByPartialMatch?.map(
          ({ makeName }) => makeName,
        );
        setChassesMakes(data?.getMakesByPartialMatch);
        setTypeaheadData({ field: 'chassisMakeCode', values });
      },
    },
  );

  const [getChassisModelsByPartialName] = useLazyQuery(
    GET_MODELS_BY_PARTIAL_MATCH,
    {
      onCompleted: (data) => {
        const values = data?.getModelsByPartialMatch?.map(
          ({ modelName }) => modelName,
        );
        setChassesModels(data?.getModelsByPartialMatch);
        setTypeaheadData({ field: 'chassisModelCode', values });
      },
    },
  );

  const getUniqueFilterElements = (parentProperty, childProperty) => {
    const isYear = childProperty !== 'solicitationNumber';
    const uniqueElements = new Set();
    filterStructureData?.forEach((item) => {
      if (parentProperty === 'soliciation')
        uniqueElements.add(item.soliciation[childProperty]);
      else
        item.BidLines?.forEach((line) => {
          if (line[childProperty]) uniqueElements.add(line[childProperty]);
        });
    });
    const options = [...uniqueElements]
      .sort((a, b) => (isYear ? -1 : 1) * (a < b ? -1 : 1))
      .map((element) => ({
        value: element,
        label: element,
      }));
    if (isYear) options.unshift({ value: 'select', label: '-Select-' });
    return options;
  };

  const contractYears = getUniqueFilterElements('soliciation', 'contractYear');

  const solicitations = getUniqueFilterElements(
    'soliciation',
    'solicitationNumber',
  );

  const modelYears = getUniqueFilterElements('BidLines', 'modelYear');

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const filteredSolicitationsWithDelay = async (filterObj) => {
    const lowerCaseFilterValue = filterObj.value.toLowerCase();
    const filteredData = [
      ...new Set(
        solicitations
          .filter((item) =>
            item.value.toLowerCase().includes(lowerCaseFilterValue),
          )
          .map((item) => item.value),
      ),
    ];
    await delay(50);

    return filteredData;
  };

  const filterStandardItemsWithDelay = async (filterObj) => {
    const lowerCaseFilterValue = filterObj.value.toLowerCase();
    const filteredData = StandardItems.filter((item) =>
      item.sin.toLowerCase().includes(lowerCaseFilterValue),
    ).map((item) => item.sin);
    await delay(50);

    return filteredData;
  };

  const handleFetchTypeaheads = ({ variables: queryVariables }) => {
    const selectedKey = queryVariables?.field;
    const filterObj = queryVariables?.filters[0]?.conditions?.find(
      (filter) => filter.key === selectedKey,
    );

    if (filterObj?.key === 'makeCode') {
      getMakesByPartialName({
        variables: {
          makeName: filterObj?.value,
          context: CONTEXT,
        },
      });
    }

    if (filterObj?.key === 'chassisMakeCode') {
      getChassisMakesByPartialName({
        variables: {
          makeName: filterObj?.value,
          context: CONTEXT,
        },
      });
    }

    if (filterObj?.key === 'modelCode') {
      getModelsByPartialName({
        variables: {
          makeCode: parseInt(selectedMakeCode, 10),
          modelName: filterObj?.value,
          context: CONTEXT,
        },
      });
    }

    if (filterObj?.key === 'chassisModelCode') {
      getChassisModelsByPartialName({
        variables: {
          makeCode: parseInt(selectedChassisMakeCode, 10),
          modelName: filterObj?.value,
          context: CONTEXT,
        },
      });
    }

    if (filterObj?.key === 'soliciation.solicitationNumber') {
      filteredSolicitationsWithDelay(filterObj).then((result) => {
        setTypeaheadData({
          field: 'soliciation.solicitationNumber',
          values: result,
        });
      });
    }

    if (filterObj?.key === 'standardItemId') {
      filterStandardItemsWithDelay(filterObj).then((result) => {
        setTypeaheadData({
          field: 'standardItemId',
          values: result,
        });
      });
    }
  };

  const handleFiltersChange = (updatedFilters) => {
    const filters = updatedFilters.flatMap((filter) => {
      if (filter?.key === 'standardItemId') {
        const ids = [
          ...(StandardItems.find((item) => item.sin === filter.value)?.ids ??
            []),
        ];
        return { key: 'standardItemId', value: ids };
      }

      if (filter?.key === 'makeCode') {
        const makeCode = makes.find(
          (make) => make.makeName === filter.value,
        )?.makeCode;
        setSelectedMakeCode(makeCode);
        return {
          key: 'makeCode',
          value: parseInt(makeCode, 10),
        };
      }

      if (filter?.key === 'chassisMakeCode') {
        const { makeCode } = chassesMakes.find(
          (make) => make.makeName === filter.value,
        );
        setSelectedChassisMakeCode(makeCode);
        return {
          key: 'chassisMakeCode',
          value: parseInt(makeCode, 10),
        };
      }

      if (filter?.key === 'modelCode') {
        const modelCode = models.find(
          (m) => m.modelName === filter.value,
        )?.modelCode;
        return {
          key: 'modelCode',
          value: modelCode,
        };
      }

      if (filter?.key === 'chassisModelCode') {
        const modelCode = chassesModels.find(
          (m) => m.modelName === filter.value,
        )?.modelCode;
        return {
          key: 'chassisModelCode',
          value: modelCode,
        };
      }

      if (filter?.key === 'modelYear') {
        return {
          key: 'modelYear',
          value: parseInt(filter.value, 10),
        };
      }

      if (filter?.key === 'soliciation.contractYear') {
        return {
          key: 'soliciation.contractYear',
          value: parseInt(filter.value, 10),
        };
      }

      if (filter?.key === 'soliciation.solicitationNumber') {
        return {
          key: 'soliciation.solicitationNumber',
          value: filter.value,
        };
      }

      return filter;
    });
    setFilters(filters);
  };

  return (
    <FilterPanel.FilterPanel
      filterStructure={[
        {
          key: 'soliciation.solicitationNumber',
          title: 'Solicitation uPIID',
          id: 'solicitationNumber',
          placeholder: 'Search solicitations',
          type: 'typeahead',
          operator: '$exact',
          value: '',
          expanded: true,
          hideClear: true,
          customFieldProps: { inputCharNum: 1 },
        },
        {
          key: 'soliciation.contractYear',
          title: 'Contract year',
          placeholder: '-Select-',
          type: 'select',
          options: contractYears,
          value: '',
          operator: 'EQ',
          expanded: true,
          hideClear: true,
        },
        {
          key: 'standardItemId',
          title: 'Standard Item',
          id: 'standardItemNumber',
          placeholder: 'Search standard items',
          type: 'typeahead',
          operator: '$exact',
          value: '',
          expanded: true,
          hideClear: true,
          customFieldProps: { inputCharNum: 1, clearPanelFilterOnEmpty: true },
        },
        {
          key: 'makeCode',
          title: 'Body make',
          type: 'typeahead',
          id: 'bodyMake',
          placeholder: 'Search body make',
          operator: '$exact',
          value: '',
          expanded: true,
          hideClear: true,
          customFieldProps: { inputCharNum: 1, clearPanelFilterOnEmpty: true },
        },
        {
          key: 'modelCode',
          title: 'Body model',
          type: 'typeahead',
          id: 'bModel',
          placeholder: 'Search body model',
          operator: '$exact',
          value: '',
          expanded: true,
          hideClear: true,
          customFieldProps: { inputCharNum: 1, clearPanelFilterOnEmpty: true },
        },
        {
          title: 'Chassis make',
          key: 'chassisMakeCode',
          id: 'chassisMake',
          placeholder: 'Search chassis make',
          type: 'typeahead',
          operator: '$exact',
          value: '',
          expanded: true,
          customFieldProps: {
            inputCharNum: 1,
            clearPanelFilterOnEmpty: true,
          },
        },
        {
          title: 'Chassis model',
          key: 'chassisModelCode',
          id: 'chassisModel',
          placeholder: 'Search chassis model',
          type: 'typeahead',
          operator: '$exact',
          value: '',
          expanded: true,
          customFieldProps: {
            inputCharNum: 1,
            clearPanelFilterOnEmpty: true,
          },
        },
        {
          key: 'modelYear',
          title: 'Model year',
          placeholder: '-Select-',
          type: 'select',
          options: modelYears,
          value: '',
          operator: 'EQ',
          expanded: true,
          hideClear: true,
        },
      ]}
      clearButtonLabel="Reset All"
      showClearIcon
      setQueryFiltersState={handleFiltersChange}
      model="bid"
      order={[['id', 'ASC']]}
      subTitle="Apply filters"
      titleSeparator
      showSearchIcon
      typeaheadData={typeaheadData}
      fetchTypeaheads={handleFetchTypeaheads}
    />
  );
};

BidHistoryTableFilter.propTypes = {
  filterStructureData: PropTypes.arrayOf(PropTypes.object).isRequired,
  setFilters: PropTypes.func.isRequired,
};

export default BidHistoryTableFilter;
